import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Menu, 
  QrCode, 
  BookOpen, 
  Megaphone,
  Zap,
  Palette,
  Headphones,
  DollarSign,
  Mail,
  Phone,
  Globe,
  ChevronDown,
  X,
  Instagram,
  Facebook,
  Sparkles,
  ArrowRight,
  Target,
  Award,
  Users,
  TrendingUp,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo } from '../data/translations';

const LandingPage = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMenuOpen(false);
    }
  };

  const languages = [
    { code: 'tn', label: 'العربية', flag: '🇹🇳' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' }
  ];

  const services = [
    {
      icon: Menu,
      title: t.services.menu.title,
      description: t.services.menu.description,
      gradient: 'from-[#6695ad]/20 to-[#c5dce9]/30',
      color: '#27466b'
    },
    {
      icon: BookOpen,
      title: t.services.catalog.title,
      description: t.services.catalog.description,
      gradient: 'from-[#c5dce9]/30 to-[#6695ad]/20',
      color: '#6695ad'
    },
    {
      icon: QrCode,
      title: t.services.qr.title,
      description: t.services.qr.description,
      gradient: 'from-[#27466b]/10 to-[#6695ad]/20',
      color: '#27466b'
    },
    {
      icon: Megaphone,
      title: t.services.marketing.title,
      description: t.services.marketing.description,
      gradient: 'from-[#6695ad]/15 to-[#c5dce9]/25',
      color: '#6695ad'
    }
  ];

  const features = [
    { icon: Zap, title: t.features.feature1.title, description: t.features.feature1.description },
    { icon: Palette, title: t.features.feature2.title, description: t.features.feature2.description },
    { icon: Headphones, title: t.features.feature3.title, description: t.features.feature3.description },
    { icon: DollarSign, title: t.features.feature4.title, description: t.features.feature4.description }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#c5dce9]/10 to-white" dir={language === 'tn' ? 'rtl' : 'ltr'}>
      {/* Navbar */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-[#c5dce9]/30' : 'bg-transparent'
      }`} dir={language === 'tn' ? 'rtl' : 'ltr'}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <img 
                src="https://customer-assets.emergentagent.com/job_menu-catalyst/artifacts/7e2qdc97_Design%20sans%20titre.png" 
                alt="Scanini Logo" 
                className="h-14 w-auto object-contain"
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
              <button onClick={() => scrollToSection('home')} className="text-[#27466b] hover:text-[#6695ad] transition-colors font-medium relative group">
                {t.nav.home}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#6695ad] group-hover:w-full transition-all duration-300 rtl:right-0 rtl:left-auto"></span>
              </button>
              <button onClick={() => scrollToSection('services')} className="text-[#27466b] hover:text-[#6695ad] transition-colors font-medium relative group">
                {t.nav.services}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#6695ad] group-hover:w-full transition-all duration-300 rtl:right-0 rtl:left-auto"></span>
              </button>
              <button onClick={() => scrollToSection('about')} className="text-[#27466b] hover:text-[#6695ad] transition-colors font-medium relative group">
                {t.nav.about}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#6695ad] group-hover:w-full transition-all duration-300 rtl:right-0 rtl:left-auto"></span>
              </button>
              
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 rounded-lg border-2 border-[#6695ad]/40 hover:border-[#6695ad] hover:bg-[#c5dce9]/20 transition-all duration-300"
                >
                  <Globe className="w-4 h-4 text-[#27466b]" />
                  <span className="text-sm font-medium text-[#27466b]">
                    {languages.find(l => l.code === language)?.label}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-[#27466b] transition-transform duration-300 ${isLangOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isLangOpen && (
                  <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border-2 border-[#c5dce9] py-2 min-w-[150px] animate-in fade-in slide-in-from-top-2 duration-200" dir={language === 'tn' ? 'rtl' : 'ltr'}>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-[#c5dce9]/30 transition-colors flex items-center space-x-2 rtl:space-x-reverse ${
                          language === lang.code ? 'bg-[#6695ad]/10 text-[#27466b] font-semibold' : 'text-[#27466b]'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-[#27466b] hover:bg-[#c5dce9]/30 rounded-lg transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t-2 border-[#c5dce9] shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-3 text-[#27466b] hover:bg-[#c5dce9]/30 rounded-lg transition-colors font-medium rtl:text-right">
                {t.nav.home}
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-3 text-[#27466b] hover:bg-[#c5dce9]/30 rounded-lg transition-colors font-medium rtl:text-right">
                {t.nav.services}
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-3 text-[#27466b] hover:bg-[#c5dce9]/30 rounded-lg transition-colors font-medium rtl:text-right">
                {t.nav.about}
              </button>
              <div className="flex space-x-2 rtl:space-x-reverse px-4 pt-2" dir={language === 'tn' ? 'rtl' : 'ltr'}>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMenuOpen(false);
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      language === lang.code 
                        ? 'bg-[#6695ad] text-white' 
                        : 'bg-[#c5dce9]/30 text-[#27466b] hover:bg-[#c5dce9]/50'
                    }`}
                  >
                    {lang.flag} {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#c5dce9]/30 via-white to-[#6695ad]/20"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#6695ad]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#c5dce9]/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 mb-6 px-6 py-3 rounded-full bg-gradient-to-r from-[#6695ad]/20 to-[#c5dce9]/30 border-2 border-[#6695ad]/30 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-[#27466b]" />
              <span className="text-[#27466b] font-semibold text-sm tracking-wide uppercase">
                {t.hero.tagline}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-[#27466b] mb-6 leading-tight animate-in fade-in slide-in-from-bottom-4 duration-700">
              {t.hero.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-[#6695ad] mb-10 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700" style={{animationDelay: '100ms'}}>
              {t.hero.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-in fade-in slide-in-from-bottom-4 duration-700" style={{animationDelay: '200ms'}}>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="group bg-gradient-to-r from-[#27466b] to-[#6695ad] hover:shadow-2xl hover:shadow-[#6695ad]/40 text-white px-8 py-7 text-lg rounded-xl transition-all duration-300 hover:scale-105 border-0"
              >
                {t.hero.cta}
                <ArrowRight className={`w-5 h-5 ${language === 'tn' ? 'mr-2' : 'ml-2'} group-hover:translate-x-1 transition-transform`} />
              </Button>
              <Button 
                onClick={() => scrollToSection('services')}
                variant="outline"
                className="border-2 border-[#6695ad] text-[#27466b] hover:bg-[#6695ad] hover:text-white px-8 py-7 text-lg rounded-xl transition-all duration-300 hover:scale-105"
              >
                {t.hero.ctaSecondary}
              </Button>
            </div>
          </div>

          {/* Enhanced QR Code Visual */}
          <div className="mt-20 flex justify-center">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#6695ad] to-[#27466b] rounded-3xl blur-3xl opacity-20 group-hover:opacity-40 transition-all duration-500 animate-pulse"></div>
              <div className="relative bg-white/80 backdrop-blur-sm p-12 rounded-3xl shadow-2xl border-2 border-[#c5dce9] group-hover:border-[#6695ad] transition-all duration-500 group-hover:scale-105">
                <QrCode className="w-56 h-56 text-[#27466b]" strokeWidth={1.5} />
              </div>
              {/* Floating decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#6695ad]/20 rounded-full blur-xl animate-bounce" style={{animationDuration: '3s'}}></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#c5dce9]/30 rounded-full blur-xl animate-bounce" style={{animationDuration: '4s', animationDelay: '0.5s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gradient-to-b from-white via-[#c5dce9]/10 to-white relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute top-0 left-0 w-full h-full opacity-30">
          <div className="absolute top-10 right-20 w-64 h-64 bg-[#6695ad]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-[#c5dce9]/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#27466b] mb-4">
              {t.services.title}
            </h2>
            <p className="text-xl text-[#6695ad] max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-[#c5dce9] hover:border-[#6695ad] overflow-hidden backdrop-blur-sm bg-white/80"
              >
                <CardContent className="p-8 relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-50 group-hover:opacity-70 transition-opacity duration-500`}></div>
                  <div className="relative z-10">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                      <service.icon className="w-10 h-10" style={{color: service.color}} />
                    </div>
                    <h3 className="text-xl font-bold text-[#27466b] mb-4 group-hover:text-[#6695ad] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-[#27466b]/80 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Us / Clients Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,#c5dce9_0%,transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#27466b] mb-6">
              {t.about.title}
            </h2>
            <p className="text-lg md:text-xl text-[#6695ad] max-w-3xl mx-auto leading-relaxed mb-12">
              {t.about.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {/* Vision Card */}
            <Card className="border-2 border-[#c5dce9] hover:border-[#6695ad] transition-all duration-300 hover:shadow-2xl backdrop-blur-sm bg-white/90">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 rtl:space-x-reverse">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#6695ad]/20 to-[#c5dce9]/30 flex items-center justify-center flex-shrink-0">
                    <Target className="w-8 h-8 text-[#27466b]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-xl text-[#27466b] mb-3">
                      {t.about.mission}
                    </h3>
                    <p className="text-[#27466b]/80 leading-relaxed">
                      {t.about.missionText}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mission Card */}
            <Card className="border-2 border-[#c5dce9] hover:border-[#6695ad] transition-all duration-300 hover:shadow-2xl backdrop-blur-sm bg-white/90">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 rtl:space-x-reverse">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#6695ad]/20 to-[#c5dce9]/30 flex items-center justify-center flex-shrink-0">
                    <Award className="w-8 h-8 text-[#27466b]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-xl text-[#27466b] mb-3">
                      {t.about.vision}
                    </h3>
                    <p className="text-[#27466b]/80 leading-relaxed">
                      {t.about.visionText}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Our Clients Section */}
          <div className="text-center">
            <h3 className="text-3xl font-bold text-[#27466b] mb-4">
              {t.clients.title}
            </h3>
            <p className="text-lg text-[#6695ad] mb-12">
              {t.clients.subtitle}
            </p>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              <div className="text-center group">
                <div className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-[#6695ad]/20 to-[#c5dce9]/30 items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-10 h-10 text-[#27466b]" />
                </div>
                <div className="text-4xl font-bold text-[#27466b] mb-2">50+</div>
                <div className="text-[#6695ad] font-medium">
                  {language === 'tn' ? 'زبون راضي' : language === 'fr' ? 'Clients Satisfaits' : 'Happy Clients'}
                </div>
              </div>

              <div className="text-center group">
                <div className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-[#6695ad]/20 to-[#c5dce9]/30 items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <QrCode className="w-10 h-10 text-[#27466b]" />
                </div>
                <div className="text-4xl font-bold text-[#27466b] mb-2">30+</div>
                <div className="text-[#6695ad] font-medium">
                  {language === 'tn' ? 'قائمة رقمية' : language === 'fr' ? 'Menus Numériques' : 'Digital Menus'}
                </div>
              </div>

              <div className="text-center group">
                <div className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-[#6695ad]/20 to-[#c5dce9]/30 items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-10 h-10 text-[#27466b]" />
                </div>
                <div className="text-4xl font-bold text-[#27466b] mb-2">95%</div>
                <div className="text-[#6695ad] font-medium">
                  {language === 'tn' ? 'رضا العملاء' : language === 'fr' ? 'Satisfaction' : 'Satisfaction Rate'}
                </div>
              </div>

              <div className="text-center group">
                <div className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-[#6695ad]/20 to-[#c5dce9]/30 items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-10 h-10 text-[#27466b]" />
                </div>
                <div className="text-4xl font-bold text-[#27466b] mb-2">3+</div>
                <div className="text-[#6695ad] font-medium">
                  {language === 'tn' ? 'سنوات خبرة' : language === 'fr' ? 'Années d\'Expérience' : 'Years Experience'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features/Why Choose Us Section */}
      <section id="about" className="py-20 bg-gradient-to-br from-[#c5dce9]/10 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,#c5dce9_0%,transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#27466b] mb-4">
              {t.features.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="text-center group"
              >
                <div className="relative inline-block mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#6695ad] to-[#27466b] rounded-full blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                  <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#c5dce9]/40 to-[#6695ad]/30 flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg">
                    <feature.icon className="w-12 h-12 text-[#27466b]" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#27466b] mb-3 group-hover:text-[#6695ad] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-[#27466b]/70 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gradient-to-br from-[#27466b] via-[#27466b] to-[#6695ad] text-white py-16 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#6695ad]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#c5dce9]/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_menu-catalyst/artifacts/7e2qdc97_Design%20sans%20titre.png" 
                  alt="Scanini Logo" 
                  className="h-16 w-auto object-contain brightness-0 invert"
                />
              </div>
              <p className="text-[#c5dce9] leading-relaxed">
                {t.footer.tagline}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold text-xl mb-6 text-[#c5dce9]">{t.footer.quickLinks}</h3>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-white/80 hover:text-[#c5dce9] transition-colors hover:translate-x-2 rtl:hover:-translate-x-2 duration-300 rtl:text-right">
                  {t.nav.home}
                </button>
                <button onClick={() => scrollToSection('services')} className="block text-white/80 hover:text-[#c5dce9] transition-colors hover:translate-x-2 rtl:hover:-translate-x-2 duration-300 rtl:text-right">
                  {t.nav.services}
                </button>
                <button onClick={() => scrollToSection('about')} className="block text-white/80 hover:text-[#c5dce9] transition-colors hover:translate-x-2 rtl:hover:-translate-x-2 duration-300 rtl:text-right">
                  {t.nav.about}
                </button>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="font-bold text-xl mb-6 text-[#c5dce9]">
                {language === 'tn' ? 'اتصل بنا' : language === 'fr' ? 'Contactez-nous' : 'Contact Us'}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3 rtl:space-x-reverse text-white/80 hover:text-[#c5dce9] transition-colors">
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium" dir="ltr">{contactInfo.phone}</span>
                </div>
                <div className="flex items-start space-x-3 rtl:space-x-reverse text-white/80 hover:text-[#c5dce9] transition-colors">
                  <Mail className="w-5 h-5 flex-shrink-0 mt-1" />
                  <span className="font-medium break-all" dir={language === 'tn' ? 'rtl' : 'ltr'}>{contactInfo.email}</span>
                </div>
              </div>
            </div>

            {/* Social Media & WhatsApp */}
            <div>
              <h3 className="font-bold text-xl mb-6 text-[#c5dce9]">{t.footer.followUs}</h3>
              <div className="flex space-x-4 rtl:space-x-reverse" dir={language === 'tn' ? 'rtl' : 'ltr'}>
                <a
                  href={contactInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-14 h-14 rounded-xl bg-white/10 hover:bg-[#c5dce9] backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-6 border-2 border-white/20 hover:border-[#c5dce9]"
                >
                  <Instagram className="w-7 h-7 text-white group-hover:text-[#27466b] transition-colors" />
                </a>
                <a
                  href={contactInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-14 h-14 rounded-xl bg-white/10 hover:bg-[#c5dce9] backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-6 border-2 border-white/20 hover:border-[#c5dce9]"
                >
                  <Facebook className="w-7 h-7 text-white group-hover:text-[#27466b] transition-colors" />
                </a>
                <a
                  href={`https://wa.me/${contactInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-14 h-14 rounded-xl bg-white/10 hover:bg-[#25D366] backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-6 border-2 border-white/20 hover:border-[#25D366]"
                >
                  <MessageCircle className="w-7 h-7 text-white transition-colors" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t-2 border-white/10 pt-8 text-center">
            <p className="text-[#c5dce9]/80" dir={language === 'tn' ? 'rtl' : 'ltr'}>{t.footer.rights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
